import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { logger } from '@fiverr-private/obs';
import * as SearchSelector from '../../selectors/Search';
import { trimAndEscapeForRegExp } from '../../utils';

/**
 * Max count matching to prevent infinite loop
 * @type {number}
 */
const MAX_COUNT_FAILSAFE = 500;

/**
 * Split a string by pattern
 * @param {string} str The string to match the pattern for (a suggestion term)
 * @param {string} subStr The string pattern to split by (user's input)
 * @returns {object[]} splits
 * @returns {boolean} splits[].match
 * @returns {string} splits[].value
 * @example
 *
 * splitByPattern('hello lola', 'lo')
 *
 *     => [
 *          { match: false, value: 'hel' },
 *          { match: true, value: 'lo' },
 *          { match: false, value: ' ' },
 *          { match: true, value: 'lo' },
 *          { match: false, value: 'la' }
 *       ]
 */
export const splitByPattern = (str, subStr) => {
    if (!str || !str.trim()) {
        return [];
    }

    const defaultResponse = [
        {
            match: false,
            value: str,
        },
    ];

    if (!subStr || !subStr.trim()) {
        return defaultResponse;
    }

    const pattern = new RegExp(trimAndEscapeForRegExp(subStr), 'g');
    const res = [];

    let match = pattern.exec(str);
    let last = 0;
    let countFailsafe = 0;

    while (match) {
        if (countFailsafe > MAX_COUNT_FAILSAFE) {
            logger.error(
                `search_bar_package/src/components/SearchHighLighter/index.js splitByPattern Infinite loop for str: ${str} and subStr: ${subStr}.`
            );

            return defaultResponse;
        }

        if (match.index > 0) {
            res.push({
                match: false,
                value: str.substring(last, match.index),
            });
        }

        res.push({
            match: true,
            value: str.substring(match.index, pattern.lastIndex),
        });

        last = pattern.lastIndex;
        match = pattern.exec(str);

        countFailsafe++;
    }

    if (last < str.length) {
        res.push({
            match: false,
            value: str.substring(last),
        });
    }

    return res;
};

const HighLighter = ({ text, pattern }) => (
    <span>
        {splitByPattern(text, pattern).map(({ match, value }, index) =>
            match || pattern === '' ? <span key={index}>{value}</span> : <b key={index}>{value}</b>
        )}
    </span>
);

HighLighter.propTypes = {
    text: PropTypes.string,
    pattern: PropTypes.string,
};

HighLighter.defaultProps = {
    text: '',
    pattern: '',
};

const mapStateToProps = (state, { text }) => ({
    text,
    pattern: SearchSelector.search(state),
});

export default connect(mapStateToProps)(HighLighter);
